import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField
} from '@mui/material';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Treemap
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { formatDistanceToNow, format, subMonths } from 'date-fns';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../../firebase';
import DashboardMetricCard from './DashboardMetricCard';

interface UserMetricsTabProps {
  organizationId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Interface for event attendance
interface EventAttendance {
  eventId?: string;
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  ticketType?: string;
  status?: string;
  attendeeId?: string;
  importDate: string;
  notes?: string;
}

// Extended contact interface with additional analytics data
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
  engagementScore: number;
  lastContactDate?: Date;
  source: string;
  createdAt: Date;
  consentStatus: 'granted' | 'denied' | 'unknown';
  eventAttendance?: EventAttendance[];
}

// Email domain analysis result interface
interface DomainAnalysis {
  domain: string;
  count: number;
  category: 'business' | 'personal' | 'education' | 'government' | 'nonprofit' | 'other';
  percentage: number;
}

// Company analysis interface
interface CompanyAnalysis {
  company: string;
  count: number;
  percentage: number;
  topContacts: Contact[];
}

// Event attendance metrics interface
interface EventMetrics {
  totalAttendances: number;
  uniqueAttendees: number;
  returnRate: number;
  averageEventsPerContact: number;
  mostPopularEvents: {
    eventName: string;
    attendeeCount: number;
  }[];
  returnAttendeeDistribution: {
    eventCount: number; // Number of events attended
    contactCount: number; // Number of contacts who attended this many events
  }[];
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`metrics-tabpanel-${index}`}
      aria-labelledby={`metrics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `metrics-tab-${index}`,
    'aria-controls': `metrics-tabpanel-${index}`,
  };
}

// Main component
const UserMetricsTab: React.FC<UserMetricsTabProps> = ({ organizationId }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [internalTabValue, setInternalTabValue] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  
  // Analytics state
  const [domainAnalysis, setDomainAnalysis] = useState<DomainAnalysis[]>([]);
  const [companyAnalysis, setCompanyAnalysis] = useState<CompanyAnalysis[]>([]);
  const [eventMetrics, setEventMetrics] = useState<EventMetrics>({
    totalAttendances: 0,
    uniqueAttendees: 0,
    returnRate: 0,
    averageEventsPerContact: 0,
    mostPopularEvents: [],
    returnAttendeeDistribution: []
  });
  
  // Time period filter for analytics
  const [timePeriod, setTimePeriod] = useState<string>('all');
  
  // Fetch contacts data and calculate metrics
  useEffect(() => {
    if (!organizationId) {
      setError("Organization ID is required");
      setLoading(false);
      return;
    }
    
    const fetchContacts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const contactsRef = collection(db, `organizations/${organizationId}/contacts`);
        const contactsSnapshot = await getDocs(contactsRef);
        
        const contactsData: Contact[] = [];
        contactsSnapshot.docs.forEach(doc => {
          try {
            const data = doc.data();
            contactsData.push({
              id: doc.id,
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              email: data.email || '',
              phone: data.phone || '',
              company: data.company || '',
              title: data.title || '',
              tags: Array.isArray(data.tags) ? data.tags : [],
              engagementScore: typeof data.engagementScore === 'number' ? data.engagementScore : 0,
              lastContactDate: data.lastContactDate ? new Date(data.lastContactDate.seconds * 1000) : undefined,
              source: data.source || 'Direct Entry',
              createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date(),
              consentStatus: data.consentStatus || 'unknown',
              eventAttendance: Array.isArray(data.eventAttendance) ? data.eventAttendance : 
                data.eventName ? [{
                  eventName: data.eventName,
                  eventDate: data.eventDate || 'Unknown',
                  importDate: data.importDate || 'Unknown'
                }] : []
            });
          } catch (err) {
            console.error("Error processing contact document:", err);
          }
        });
        
        setContacts(contactsData);
        
        // Calculate analytics
        calculateDomainAnalytics(contactsData);
        calculateCompanyAnalytics(contactsData);
        calculateEventMetrics(contactsData);
        
      } catch (err) {
        console.error("Error fetching contacts:", err);
        setError("Failed to load contacts data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchContacts();
  }, [organizationId, timePeriod]);
  
  // Calculate domain analytics from contacts
  const calculateDomainAnalytics = (contactsData: Contact[]) => {
    // Skip contacts without emails
    const contactsWithEmail = contactsData.filter(contact => contact.email && contact.email.includes('@'));
    
    if (contactsWithEmail.length === 0) {
      setDomainAnalysis([]);
      return;
    }
    
    // Extract domain from email and count occurrences
    const domains: Record<string, number> = {};
    contactsWithEmail.forEach(contact => {
      const domain = contact.email.split('@')[1].toLowerCase();
      domains[domain] = (domains[domain] || 0) + 1;
    });
    
    // Categorize domains
    const categorizedDomains: DomainAnalysis[] = Object.entries(domains).map(([domain, count]) => {
      let category: 'business' | 'personal' | 'education' | 'government' | 'nonprofit' | 'other' = 'other';
      
      // Common personal email domains
      if (['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com'].includes(domain)) {
        category = 'personal';
      } 
      // Education domains
      else if (domain.endsWith('.edu') || domain.includes('school') || domain.includes('university') || domain.includes('college')) {
        category = 'education';
      } 
      // Government domains
      else if (domain.endsWith('.gov') || domain.includes('government')) {
        category = 'government';
      } 
      // Nonprofit domains
      else if (domain.endsWith('.org') || domain.includes('nonprofit') || domain.includes('ngo')) {
        category = 'nonprofit';
      } 
      // Business domains - everything else is likely a business
      else if (domain.endsWith('.com') || domain.endsWith('.co') || domain.endsWith('.io') || domain.endsWith('.net')) {
        category = 'business';
      }
      
      return {
        domain,
        count,
        category,
        percentage: (count / contactsWithEmail.length) * 100
      };
    });
    
    // Sort by count, descending
    categorizedDomains.sort((a, b) => b.count - a.count);
    
    setDomainAnalysis(categorizedDomains);
  };
  
  // Calculate company analytics from contacts
  const calculateCompanyAnalytics = (contactsData: Contact[]) => {
    // Skip contacts without company info
    const contactsWithCompany = contactsData.filter(contact => contact.company && contact.company.trim() !== '');
    
    if (contactsWithCompany.length === 0) {
      setCompanyAnalysis([]);
      return;
    }
    
    // Group contacts by company
    const companiesMap: Record<string, Contact[]> = {};
    contactsWithCompany.forEach(contact => {
      const companyName = contact.company!.trim().toLowerCase();
      if (!companiesMap[companyName]) {
        companiesMap[companyName] = [];
      }
      companiesMap[companyName].push(contact);
    });
    
    // Create company analysis
    const companies: CompanyAnalysis[] = Object.entries(companiesMap).map(([company, contacts]) => {
      const normalizedCompany = company.charAt(0).toUpperCase() + company.slice(1);
      return {
        company: normalizedCompany,
        count: contacts.length,
        percentage: (contacts.length / contactsWithCompany.length) * 100,
        topContacts: [...contacts].sort((a, b) => b.engagementScore - a.engagementScore).slice(0, 3)
      };
    });
    
    // Sort by count, descending
    companies.sort((a, b) => b.count - a.count);
    
    setCompanyAnalysis(companies);
  };
  
  // Calculate event metrics from contacts
  const calculateEventMetrics = (contactsData: Contact[]) => {
    // Get contacts with event attendance data
    const contactsWithEvents = contactsData.filter(
      contact => contact.eventAttendance && contact.eventAttendance.length > 0
    );
    
    if (contactsWithEvents.length === 0) {
      setEventMetrics({
        totalAttendances: 0,
        uniqueAttendees: 0,
        returnRate: 0,
        averageEventsPerContact: 0,
        mostPopularEvents: [],
        returnAttendeeDistribution: []
      });
      return;
    }
    
    // Create a map of unique events by normalizing event names
    const normalizeEventName = (name: string): string => {
      return name.trim().toLowerCase();
    };
    
    // Track unique events per contact
    const contactEventMap: Map<string, Set<string>> = new Map();
    
    // Count total attendances and track unique events per contact
    let totalAttendances = 0;
    
    contactsWithEvents.forEach(contact => {
      if (!contactEventMap.has(contact.id)) {
        contactEventMap.set(contact.id, new Set());
      }
      
      // Add each event to the contact's set of unique attended events
      contact.eventAttendance!.forEach(attendance => {
        const normalizedEventName = normalizeEventName(attendance.eventName);
        contactEventMap.get(contact.id)!.add(normalizedEventName);
      });
      
      // Count raw attendance records for total attendance metric
      totalAttendances += contact.eventAttendance!.length;
    });
    
    // Calculate return rate (contacts attending more than one unique event)
    const multipleEventAttendees = Array.from(contactEventMap.values()).filter(eventSet => eventSet.size > 1);
    const returnRate = (multipleEventAttendees.length / contactsWithEvents.length) * 100;
    
    // Calculate average unique events per contact
    const totalUniqueEventAttendances = Array.from(contactEventMap.values())
      .reduce((sum, eventSet) => sum + eventSet.size, 0);
    const averageEventsPerContact = totalUniqueEventAttendances / contactsWithEvents.length;
    
    // Most popular events (counting each contact only once per event)
    const eventContactCounts: Record<string, Set<string>> = {};
    contactsWithEvents.forEach(contact => {
      contact.eventAttendance!.forEach(attendance => {
        const normalizedEventName = normalizeEventName(attendance.eventName);
        if (!eventContactCounts[normalizedEventName]) {
          eventContactCounts[normalizedEventName] = new Set();
        }
        eventContactCounts[normalizedEventName].add(contact.id);
      });
    });
    
    const popularEvents = Object.entries(eventContactCounts)
      .map(([eventName, contactIds]) => ({ 
        eventName,
        attendeeCount: contactIds.size
      }))
      .sort((a, b) => b.attendeeCount - a.attendeeCount)
      .slice(0, 5);
    
    // Distribution of return attendees
    const eventCountDistribution: Record<number, number> = {};
    contactEventMap.forEach((eventSet) => {
      const uniqueEventCount = eventSet.size;
      eventCountDistribution[uniqueEventCount] = (eventCountDistribution[uniqueEventCount] || 0) + 1;
    });
    
    const distribution = Object.entries(eventCountDistribution)
      .map(([eventCount, contactCount]) => ({
        eventCount: parseInt(eventCount),
        contactCount
      }))
      .sort((a, b) => a.eventCount - b.eventCount);
    
    setEventMetrics({
      totalAttendances,
      uniqueAttendees: contactsWithEvents.length,
      returnRate,
      averageEventsPerContact,
      mostPopularEvents: popularEvents,
      returnAttendeeDistribution: distribution
    });
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setInternalTabValue(newValue);
  };
  
  // Handle time period change
  const handleTimePeriodChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setTimePeriod(event.target.value as string);
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          Audience Insights
        </Typography>
        
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="time-period-label">Time Period</InputLabel>
          <Select
            labelId="time-period-label"
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            label="Time Period"
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="30days">Last 30 Days</MenuItem>
            <MenuItem value="90days">Last 90 Days</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Audience Metrics Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="Total Contacts"
            value={contacts.length}
            icon="people"
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="Business Emails"
            value={domainAnalysis.filter(d => d.category === 'business').reduce((sum, item) => sum + item.count, 0)}
            icon="business"
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="Companies Represented"
            value={companyAnalysis.length}
            icon="domain"
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            title="Return Guest Rate"
            value={`${eventMetrics.returnRate.toFixed(1)}%`}
            icon="repeat"
            color="#9c27b0"
          />
        </Grid>
      </Grid>
      
      {/* Tabs for different analytics views */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={internalTabValue} 
            onChange={handleTabChange} 
            aria-label="user metrics tabs"
          >
            <Tab label="Attendance" {...a11yProps(0)} />
            <Tab label="Email Analysis" {...a11yProps(1)} />
            <Tab label="Companies" {...a11yProps(2)} />
          </Tabs>
        </Box>
        
        {/* Tab 1: Event Attendance Analytics */}
        <TabPanel value={internalTabValue} index={0}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Event Attendance Metrics
            </Typography>
            
            <Grid container spacing={3}>
              {/* First row of metrics */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Total Attendances
                    </Typography>
                    <Typography variant="h3">
                      {eventMetrics.totalAttendances}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {eventMetrics.uniqueAttendees} unique attendees
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Return Rate
                    </Typography>
                    <Typography variant="h3">
                      {eventMetrics.returnRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Attendees who came to multiple events
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Avg. Events Per Attendee
                    </Typography>
                    <Typography variant="h3">
                      {eventMetrics.averageEventsPerContact.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Higher is better for engagement
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Most Popular Events */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Most Popular Events
                    </Typography>
                    
                    {eventMetrics.mostPopularEvents.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={eventMetrics.mostPopularEvents}
                          margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="eventName" 
                            angle={-45} 
                            textAnchor="end"
                            height={70}
                          />
                          <YAxis />
                          <RechartsTooltip formatter={(value, name) => [`${value} attendees`, 'Attendance']} />
                          <Bar dataKey="attendeeCount" fill="#8884d8" name="Attendees" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography>No event data available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Return Attendee Distribution */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Attendance Distribution
                    </Typography>
                    
                    {eventMetrics.returnAttendeeDistribution.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={eventMetrics.returnAttendeeDistribution}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="eventCount" 
                            label={{ value: 'Number of Events Attended', position: 'insideBottom', offset: -5 }}
                          />
                          <YAxis label={{ value: 'Number of Contacts', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip formatter={(value, name) => [`${value} contacts`, 'Count']} />
                          <Bar dataKey="contactCount" fill="#82ca9d" name="Contacts" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography>No event data available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
        
        {/* Tab 2: Email Analytics */}
        <TabPanel value={internalTabValue} index={1}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Email Domain Analysis
            </Typography>
            
            <Grid container spacing={3}>
              {/* Domain Category Distribution */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Domain Categories
                    </Typography>
                    
                    {domainAnalysis.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Business', value: domainAnalysis.filter(d => d.category === 'business').reduce((sum, item) => sum + item.count, 0) },
                              { name: 'Personal', value: domainAnalysis.filter(d => d.category === 'personal').reduce((sum, item) => sum + item.count, 0) },
                              { name: 'Education', value: domainAnalysis.filter(d => d.category === 'education').reduce((sum, item) => sum + item.count, 0) },
                              { name: 'Government', value: domainAnalysis.filter(d => d.category === 'government').reduce((sum, item) => sum + item.count, 0) },
                              { name: 'Nonprofit', value: domainAnalysis.filter(d => d.category === 'nonprofit').reduce((sum, item) => sum + item.count, 0) },
                              { name: 'Other', value: domainAnalysis.filter(d => d.category === 'other').reduce((sum, item) => sum + item.count, 0) }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#1976d2" /> {/* Business */}
                            <Cell fill="#f44336" /> {/* Personal */}
                            <Cell fill="#4caf50" /> {/* Education */}
                            <Cell fill="#9c27b0" /> {/* Government */}
                            <Cell fill="#ff9800" /> {/* Nonprofit */}
                            <Cell fill="#607d8b" /> {/* Other */}
                          </Pie>
                          <RechartsTooltip formatter={(value, name) => {
                            const numValue = typeof value === 'number' ? value : 0;
                            return [`${numValue} contacts (${((numValue / contacts.length) * 100).toFixed(1)}%)`, name];
                          }} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography>No email domain data available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Top Domains Table */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Email Domains
                    </Typography>
                    
                    {domainAnalysis.length > 0 ? (
                      <TableContainer sx={{ maxHeight: 300 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Domain</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell align="right">Count</TableCell>
                              <TableCell align="right">Percentage</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {domainAnalysis.slice(0, 10).map((domain) => (
                              <TableRow key={domain.domain} hover>
                                <TableCell>{domain.domain}</TableCell>
                                <TableCell>
                                  <Chip 
                                    size="small" 
                                    label={domain.category.charAt(0).toUpperCase() + domain.category.slice(1)} 
                                    color={
                                      domain.category === 'business' ? 'primary' :
                                      domain.category === 'personal' ? 'error' :
                                      domain.category === 'education' ? 'success' :
                                      domain.category === 'government' ? 'secondary' :
                                      domain.category === 'nonprofit' ? 'warning' : 'default'
                                    } 
                                    variant="outlined" 
                                  />
                                </TableCell>
                                <TableCell align="right">{domain.count}</TableCell>
                                <TableCell align="right">{domain.percentage.toFixed(1)}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography>No email domain data available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
        
        {/* Tab 3: Company Analytics */}
        <TabPanel value={internalTabValue} index={2}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Company Analysis
            </Typography>
            
            <Grid container spacing={3}>
              {/* Top Companies */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Companies
                    </Typography>
                    
                    {companyAnalysis.length > 0 ? (
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Company</TableCell>
                              <TableCell align="right">Contacts</TableCell>
                              <TableCell align="right">Percentage</TableCell>
                              <TableCell>Top Contacts</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {companyAnalysis.slice(0, 20).map((company) => (
                              <TableRow key={company.company} hover>
                                <TableCell>{company.company}</TableCell>
                                <TableCell align="right">{company.count}</TableCell>
                                <TableCell align="right">{company.percentage.toFixed(1)}%</TableCell>
                                <TableCell>
                                  {company.topContacts.map((contact) => (
                                    <Chip 
                                      key={contact.id}
                                      size="small" 
                                      label={`${contact.firstName} ${contact.lastName}`} 
                                      sx={{ margin: 0.5 }}
                                    />
                                  ))}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography>No company data available</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default UserMetricsTab;
